import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { hasCrossLevelOr, resolveIndustryDefaultBrand } from "@nexus/contracts";
import type { DrilldownFilterPresetDto, FilterPresetDto } from "@nexus/contracts";
import { resolveCompetitorBrandSelections } from "@nexus/dashboard-kpis/competitor-filter-defaults";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { isOverviewDirty, useOverviewFiltersStore } from "@/features/overview/stores/overviewFilters.store";
import { OverviewFilterBar } from "@/features/overview/components/OverviewFilterBar";
import {
  appliedDrilldownToSearch,
  drilldownSearchKey,
  drilldownSearchSchema,
} from "@/features/drilldown/lib/drilldown-search";
import {
  isDrilldownDirty,
  useDrilldownFiltersStore,
} from "@/features/drilldown/stores/drilldownFilters.store";
import { DrilldownFiltersToolbar } from "@/features/drilldown/components/DrilldownFiltersToolbar";
import { ExploreApplyControls } from "../header/GlobalFilterHeader/ExploreApplyControls";
import { useExploreSearchSync } from "../../hooks/useExploreSearchSync";
import { shouldDeferFilterResetForUrlHydration, shouldHydrateStoreFromUrl } from "../../lib/explore-search";
import {
  competitorOptionsQueryOptions,
  competitorPlatformOptionsQueryOptions,
  industriesQueryOptions,
  industryProductsQueryOptions,
} from "@/lib/queries/opportunity-sets.queries";
import {
  competitorFilterDefaultsQueryOptions,
  myBrandFilterOptionsQueryOptions,
  useBootstrapCompetitorFilterDefaults,
} from "@/lib/queries/dashboard.queries";
import { tenantSettingsQueryOptions } from "@/lib/queries/tenant-settings.queries";
import { GlobalFilterHeader } from "../header/GlobalFilterHeader/GlobalFilterHeader";
import type { CompetitorFilters, MyBrandFilters } from "../../types/dashboard.types";
import { isCompetitorFiltersComplete, isMyBrandFiltersComplete } from "../../types/dashboard.types";
import { filtersKey } from "../../lib/filters";
import { useDashboardFiltersStore } from "../../stores/dashboardFilters.store";
import { useRecentAccounts } from "../../hooks/useRecentAccounts";
import { useRecentProducts } from "../../hooks/useRecentProducts";
import {
  deriveAdAccounts,
  derivePlatforms,
  deriveProducts,
  platformDisplayName,
  pruneMyBrandSelections,
  prunePlatforms,
} from "../../lib/my-brand-filter-options";
import { exploreApiPrefix, exploreDataScope } from "../../lib/explore-api-prefix";
import {
  canUseExploreMyBrand,
  EXPLORE_COMPETITOR_DEFAULTS_PUT_KEY,
  resolveExploreModeAccess,
} from "../../lib/explore-mode-access";
import {
  buildBootstrapPayload,
  findCompetitorSettingsForIndustry,
  pruneMyBrandToRoster,
  resolveStartingIndustry,
} from "../../lib/competitor-filter-defaults";
import { isCompetitorSeedSettledIncomplete } from "../../lib/competitor-selection-state";
import { withSnapshotDates } from "../../lib/explore-date-presets";
import {
  canApplyExploreFilters,
  resolveExploreApplyDisableReason,
} from "../../lib/can-apply-explore-filters";
import type { ExploreDateSelectionFields } from "../../types/dashboard.types";

export function ExploreFilterHeader() {
  const { t } = useTranslation();
  const {
    mode,
    setMode,
    competitorFilters,
    setCompetitorFilters,
    myBrandFilters,
    setMyBrandFilters,
    commitDateSelection,
    appliedCompetitorFilters,
    appliedMyBrandFilters,
    applyFilters,
    discardFilters,
    resetForTenant,
    hydrateFromApplied,
    northStar,
    setNorthStar,
    seedNorthStarDefault,
    setCompetitorSeedSettledIncomplete,
  } = useDashboardFiltersStore();

  const activeTenantId = useAuth((s) => s.activeTenantId);
  const tenants = useAuth((s) => s.tenants);
  const permissions = usePermissions();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const exploreBasePath = useMemo(() => exploreApiPrefix(pathname), [pathname]);
  const dataScope = useMemo(() => exploreDataScope(pathname), [pathname]);
  const canUseMyBrand = canUseExploreMyBrand(dataScope, permissions);
  const productLine = tenants.find((tenant) => tenant.id === activeTenantId)?.productLine;
  const modeAccess = resolveExploreModeAccess(productLine, canUseMyBrand);
  const canFetchMyBrandFilterOptions = canUseExploreMyBrand(dataScope, permissions);

  const { data: filterDefaults, isLoading: competitorDefaultsLoading } = useQuery(
    competitorFilterDefaultsQueryOptions(activeTenantId, exploreBasePath),
  );

  const { data: tenantSettings } = useQuery(tenantSettingsQueryOptions(activeTenantId));

  useEffect(() => {
    if (!activeTenantId || tenantSettings?.defaultNorthStar == null) return;
    seedNorthStarDefault(activeTenantId, tenantSettings.defaultNorthStar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId, tenantSettings?.defaultNorthStar]);
  const bootstrapDefaults = useBootstrapCompetitorFilterDefaults(exploreBasePath);

  const { data: filterOptions = { tuples: [] }, isLoading: filterOptionsLoading } = useQuery(
    myBrandFilterOptionsQueryOptions(activeTenantId, canFetchMyBrandFilterOptions, exploreBasePath),
  );
  const filterTuples = filterOptions.tuples;

  const myBrandPlatforms = useMemo(
    () => derivePlatforms(t, filterTuples),
    [t, filterTuples],
  );
  const myBrandAccounts = useMemo(
    () => deriveAdAccounts(filterTuples, myBrandFilters.platforms),
    [filterTuples, myBrandFilters.platforms],
  );
  const myBrandProducts = useMemo(
    () =>
      deriveProducts(filterTuples, myBrandFilters.platforms, myBrandFilters.accounts),
    [filterTuples, myBrandFilters.platforms, myBrandFilters.accounts],
  );
  const myBrandProductOptions = useMemo(
    () => myBrandProducts.map((p) => ({ ...p, isFocused: false })),
    [myBrandProducts],
  );

  const { recent: recentAccounts, record: onAccountRecorded } = useRecentAccounts(
    myBrandAccounts,
    activeTenantId,
  );
  const { recent: recentProducts, record: onProductRecorded } = useRecentProducts(
    myBrandProductOptions,
    activeTenantId,
  );

  const { data: industries = [], isLoading: industriesLoading } = useQuery(
    industriesQueryOptions(dataScope, activeTenantId),
  );

  const {
    data: myIndustryBrands = [],
    isLoading: myIndustryBrandsLoading,
    isError: myIndustryBrandsError,
    isSuccess: myIndustryBrandsSuccess,
  } = useQuery(
    competitorOptionsQueryOptions(dataScope, activeTenantId, competitorFilters.myIndustry),
  );

  const {
    data: competitorBrands = [],
    isLoading: competitorBrandsLoading,
    isError: competitorBrandsError,
    isSuccess: competitorBrandsSuccess,
  } = useQuery(
    competitorOptionsQueryOptions(
      dataScope,
      activeTenantId,
      competitorFilters.competitorsIndustry,
      competitorFilters.platforms,
    ),
  );

  const { data: industryProducts = [], isLoading: productsToCompareLoading } = useQuery(
    industryProductsQueryOptions(dataScope, activeTenantId, competitorFilters.myIndustry),
  );

  const competitorPlatformIndustry = useMemo(() => {
    if (!competitorFilters.competitorsIndustry) return "";
    return [competitorFilters.myIndustry, competitorFilters.competitorsIndustry]
      .filter((value): value is string => Boolean(value))
      .join(",");
  }, [competitorFilters.myIndustry, competitorFilters.competitorsIndustry]);

  const {
    data: competitorPlatformOptions,
    isLoading: competitorPlatformsLoading,
    isError: competitorPlatformsError,
  } = useQuery(
    competitorPlatformOptionsQueryOptions(
      dataScope,
      activeTenantId,
      competitorPlatformIndustry || null,
    ),
  );

  const competitorPlatforms = useMemo(
    () =>
      (competitorPlatformOptions?.platforms ?? []).map((id) => ({
        id,
        name: platformDisplayName(t, id),
      })),
    [t, competitorPlatformOptions],
  );
  const allowedCompetitorPlatformIds = useMemo(
    () => competitorPlatforms.map((p) => p.id),
    [competitorPlatforms],
  );

  useEffect(() => {
    if (!competitorPlatformOptions) return;
    const allowed = competitorPlatformOptions.platforms;
    useDashboardFiltersStore.setState((state) => {
      const draft = prunePlatforms(state.competitorFilters.platforms, allowed);
      const applied = prunePlatforms(state.appliedCompetitorFilters.platforms, allowed);
      const draftUnchanged =
        draft.length === state.competitorFilters.platforms.length &&
        draft.every((p, i) => p === state.competitorFilters.platforms[i]);
      const appliedUnchanged =
        applied.length === state.appliedCompetitorFilters.platforms.length &&
        applied.every((p, i) => p === state.appliedCompetitorFilters.platforms[i]);
      if (draftUnchanged && appliedUnchanged) return {};
      return {
        competitorFilters: { ...state.competitorFilters, platforms: draft },
        appliedCompetitorFilters: { ...state.appliedCompetitorFilters, platforms: applied },
      };
    });
  }, [competitorPlatformOptions]);

  const industryIndexes = useMemo(() => industries.map((ind) => ind.index), [industries]);
  const myIndustryBrandNames = useMemo(
    () => myIndustryBrands.map((b) => b.name),
    [myIndustryBrands],
  );
  const industryDefaultBrand = useMemo(
    () =>
      resolveIndustryDefaultBrand({
        industry: competitorFilters.myIndustry,
        industryDefaults: filterDefaults?.competitors ?? [],
        tenantDefaultBrand: filterDefaults?.defaultBrand,
        validBrandNames: myIndustryBrandNames,
      }),
    [competitorFilters.myIndustry, filterDefaults, myIndustryBrandNames],
  );
  const competitorBrandNames = useMemo(
    () => competitorBrands.map((b) => b.name),
    [competitorBrands],
  );
  const selectableAdAccountIds = useMemo(
    () => myBrandAccounts.map((a) => a.id),
    [myBrandAccounts],
  );
  const allowedMyBrandPlatformIds = useMemo(
    () => myBrandPlatforms.map((p) => p.id),
    [myBrandPlatforms],
  );
  const selectableProductCodes = useMemo(
    () => myBrandProducts.map((p) => p.id),
    [myBrandProducts],
  );

  const productFallback = useMemo(
    () => ({
      industryProducts: competitorFilters.industryProducts,
      products: myBrandFilters.products,
    }),
    [competitorFilters.industryProducts, myBrandFilters.products],
  );

  const draftKey = filtersKey(mode === "competitors" ? competitorFilters : myBrandFilters);
  const appliedKey = filtersKey(
    mode === "competitors" ? appliedCompetitorFilters : appliedMyBrandFilters,
  );
  const isDirty = draftKey !== appliedKey;

  const isOverviewRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith("/overview"),
  });
  const isDrilldownRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith("/drilldown"),
  });
  const discardOverviewFilters = useOverviewFiltersStore((s) => s.discardFilters);
  const applyDrilldownFilters = useDrilldownFiltersStore((s) => s.applyFilters);
  const discardDrilldownFilters = useDrilldownFiltersStore((s) => s.discardFilters);
  const initializeDrilldownDateRange = useDrilldownFiltersStore((s) => s.initializeDateRange);

  const navigate = useNavigate();

  const pushDrilldownAppliedToUrl = useCallback(() => {
    const drilldownState = useDrilldownFiltersStore.getState();
    const drilldownPart = appliedDrilldownToSearch(drilldownState);
    void navigate({
      search: ((prev: Record<string, unknown>) => {
        const merged = { ...prev, ...drilldownPart };
        const currentParsed = drilldownSearchSchema.parse(prev);
        const nextParsed = drilldownSearchSchema.parse(merged);
        const currentKey = drilldownSearchKey(currentParsed);
        const nextKey = drilldownSearchKey(nextParsed);
        if (currentKey === nextKey) return prev;
        return merged;
      }) as never,
      replace: true,
    });
  }, [navigate]);

  const userTouchedRef = useRef(false);

  const handleExternalUrlNavigation = useCallback(() => {
    discardFilters();
    if (isOverviewRoute) {
      discardOverviewFilters();
    }
    if (isDrilldownRoute) {
      discardDrilldownFilters();
    }
    userTouchedRef.current = false;
  }, [
    discardFilters,
    discardOverviewFilters,
    discardDrilldownFilters,
    isOverviewRoute,
    isDrilldownRoute,
  ]);

  const {
    handleModeChange,
    pushDraftToUrl,
    pushAppliedToUrl,
    markStoreInitiatedNavigation,
    parsedSearch,
    urlHydrationPhase,
  } = useExploreSearchSync({
    activeTenantId,
    mode,
    competitorFilters,
    myBrandFilters,
    appliedCompetitorFilters,
    appliedMyBrandFilters,
    isDraftDirty: isDirty,
    industries: industryIndexes,
    myIndustryBrandNames,
    competitorBrandNames,
    selectableAdAccountIds,
    allowedMyBrandPlatformIds,
    allowedCompetitorPlatformIds,
    selectableProductCodes,
    accountFallback: null,
    productFallback,
    competitorBrandsLoaded: competitorBrandsSuccess,
    hydrateFromApplied,
    onExternalUrlNavigation: handleExternalUrlNavigation,
    setMode,
  });

  useEffect(() => {
    if (modeAccess.lockedMode && mode !== modeAccess.lockedMode) {
      handleModeChange(modeAccess.lockedMode);
    }
  }, [modeAccess.lockedMode, mode, handleModeChange]);

  const seededIndustryRef = useRef<string | null>(null);
  const industrySeededRef = useRef(false);
  const bootstrappedTenantsRef = useRef<Set<string>>(new Set());
  const prevTenantIdRef = useRef<string | null>(null);

  const hasUrlMyBrand = Boolean(parsedSearch.myBrand);
  const hasUrlMainCompetitor = Boolean(parsedSearch.mainCompetitor);
  const hasUrlBenchmark = Boolean(parsedSearch.benchmark);
  const hasUrlMyIndustry = Boolean(parsedSearch.myIndustry);
  const hasUrlCompetitorsIndustry = Boolean(parsedSearch.competitorsIndustry);

  useEffect(() => {
    const isTenantSwitch =
      prevTenantIdRef.current !== null && prevTenantIdRef.current !== activeTenantId;
    prevTenantIdRef.current = activeTenantId;

    userTouchedRef.current = false;
    resetForTenant(activeTenantId, {
      hydratingFromUrl: shouldDeferFilterResetForUrlHydration(parsedSearch),
    });
    seededIndustryRef.current = null;
    industrySeededRef.current = false;
    if (activeTenantId) {
      bootstrappedTenantsRef.current.delete(activeTenantId);
    }

    if (isTenantSwitch && activeTenantId) {
      markStoreInitiatedNavigation();
      const state = useDashboardFiltersStore.getState();
      pushAppliedToUrl({
        modeOverride: state.mode,
        competitorOverride: state.competitorFilters,
        myBrandOverride: state.myBrandFilters,
        skipPendingHydrationGuard: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  useEffect(() => {
    if (mode !== "competitors") return;
    if (hasUrlMyIndustry) return;
    if (industriesLoading || competitorDefaultsLoading) return;
    if (industryIndexes.length === 0) return;
    if (industrySeededRef.current) return;

    const industry = resolveStartingIndustry({
      defaultIndustry: filterDefaults?.defaultIndustry,
      industryIndexes,
    });
    if (!industry) return;

    industrySeededRef.current = true;
    setCompetitorFilters((prev) => {
      if (prev.myIndustry !== null && prev.myIndustry !== industry) return prev;
      return {
        ...prev,
        myIndustry: industry,
        competitorsIndustry: hasUrlCompetitorsIndustry ? prev.competitorsIndustry : industry,
        myBrand: null,
        industryProducts: [],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    hasUrlMyIndustry,
    hasUrlCompetitorsIndustry,
    industriesLoading,
    competitorDefaultsLoading,
    filterDefaults?.defaultIndustry,
    industryIndexes,
  ]);

  const seededCompetitorSelections = useMemo(() => {
    const industry = competitorFilters.competitorsIndustry;
    if (!industry) return null;
    if (!competitorBrandsSuccess) return null;
    if (competitorBrands.length > 0 && !filterDefaults) return null;

    const saved = findCompetitorSettingsForIndustry(filterDefaults?.competitors ?? [], industry);
    return {
      industry,
      ...resolveCompetitorBrandSelections({
        brands: competitorBrands,
        mainCompetitor: competitorFilters.mainCompetitor,
        benchmark: competitorFilters.benchmark,
        saved,
        hasUrlMainCompetitor,
        hasUrlBenchmark,
      }),
    };
  }, [
    competitorBrands,
    competitorBrandsSuccess,
    competitorFilters.competitorsIndustry,
    competitorFilters.mainCompetitor,
    competitorFilters.benchmark,
    filterDefaults,
    hasUrlBenchmark,
    hasUrlMainCompetitor,
  ]);

  useEffect(() => {
    if (mode !== "competitors") return;
    if (!seededCompetitorSelections) return;

    const next = seededCompetitorSelections;
    setCompetitorFilters((prev) => {
      if (prev.competitorsIndustry !== next.industry) return prev;
      const mainUnchanged = prev.mainCompetitor === next.mainCompetitor;
      const benchmarkUnchanged =
        prev.benchmark.length === next.benchmark.length &&
        prev.benchmark.every((name, index) => name === next.benchmark[index]);
      if (mainUnchanged && benchmarkUnchanged) return prev;
      return { ...prev, mainCompetitor: next.mainCompetitor, benchmark: next.benchmark };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededCompetitorSelections]);

  useEffect(() => {
    if (mode !== "competitors") return;
    if (
      industryProducts.length > 0 &&
      competitorFilters.industryProducts.length === 0 &&
      competitorFilters.myIndustry !== seededIndustryRef.current
    ) {
      seededIndustryRef.current = competitorFilters.myIndustry;
      setCompetitorFilters((prev) =>
        prev.industryProducts.length === 0
          ? { ...prev, industryProducts: industryProducts.map((p) => p.id) }
          : prev,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industryProducts, competitorFilters.industryProducts.length]);

  useEffect(() => {
    if (mode !== "competitors") return;
    if (hasUrlMyBrand) return;
    if (!industryDefaultBrand || competitorFilters.myBrand !== null) return;
    setCompetitorFilters((prev) =>
      prev.myBrand === null ? { ...prev, myBrand: industryDefaultBrand } : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, industryDefaultBrand, competitorFilters.myBrand, hasUrlMyBrand]);

  const isSeedSettledIncomplete =
    mode === "competitors" &&
    isCompetitorSeedSettledIncomplete({
      draft: competitorFilters,
      isIndustriesSettled: !industriesLoading,
      isDefaultsSettled: !competitorDefaultsLoading,
      isMyIndustryBrandsSettled: myIndustryBrandsSuccess || myIndustryBrandsError,
      isCompetitorBrandsSettled: competitorBrandsSuccess || competitorBrandsError,
      projectedMyBrand: competitorFilters.myBrand ?? industryDefaultBrand,
      projectedMainCompetitor: seededCompetitorSelections
        ? seededCompetitorSelections.mainCompetitor
        : competitorFilters.mainCompetitor,
    });

  useEffect(() => {
    setCompetitorSeedSettledIncomplete(isSeedSettledIncomplete);
  }, [isSeedSettledIncomplete, setCompetitorSeedSettledIncomplete]);

  useEffect(() => {
    if (mode !== "competitors" || !myIndustryBrandsSuccess) return;
    const rosterIndustry = competitorFilters.myIndustry;
    useDashboardFiltersStore.setState((state) => {
      const prune = (filters: CompetitorFilters) =>
        pruneMyBrandToRoster({
          filters,
          rosterIndustry,
          rosterBrandNames: myIndustryBrandNames,
          fallbackBrand: industryDefaultBrand,
        });
      const draft = prune(state.competitorFilters);
      const applied = prune(state.appliedCompetitorFilters);
      if (draft === state.competitorFilters && applied === state.appliedCompetitorFilters) return {};
      return { competitorFilters: draft, appliedCompetitorFilters: applied };
    });
  }, [
    mode,
    myIndustryBrandsSuccess,
    competitorFilters.myIndustry,
    competitorFilters.myBrand,
    myIndustryBrandNames,
    industryDefaultBrand,
  ]);

  useEffect(() => {
    if (myBrandAccounts.length === 0) return;
    setMyBrandFilters((prev) => {
      if (prev.accounts === null) return prev;
      const valid = prev.accounts.filter((id) => myBrandAccounts.some((a) => a.id === id));
      if (valid.length === prev.accounts.length) return prev;
      return { ...prev, accounts: valid.length > 0 ? valid : null };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBrandAccounts, activeTenantId]);

  const prevAccountsRef = useRef<string[] | null>(myBrandFilters.accounts);
  useEffect(() => {
    const prev = prevAccountsRef.current;
    const curr = myBrandFilters.accounts;
    const changed =
      prev === null
        ? curr !== null
        : curr === null
          ? true
          : prev.length !== curr.length || [...prev].sort().some((id, i) => id !== [...curr].sort()[i]);
    prevAccountsRef.current = myBrandFilters.accounts;
    if (changed) {
      setMyBrandFilters((f) => ({ ...f, products: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBrandFilters.accounts]);

  const tryBootstrapDefaults = useCallback(
    (filters: CompetitorFilters) => {
      if (mode !== "competitors" || !activeTenantId) return;
      if (!permissions[EXPLORE_COMPETITOR_DEFAULTS_PUT_KEY[dataScope]].isOn) return;
      if (bootstrappedTenantsRef.current.has(activeTenantId)) return;
      if (!isCompetitorFiltersComplete(filters)) return;
      if (shouldHydrateStoreFromUrl(parsedSearch) && shouldDeferFilterResetForUrlHydration(parsedSearch)) {
        return;
      }

      const payload = buildBootstrapPayload({
        defaults: filterDefaults,
        appliedFilters: filters,
      });

      if (!payload) {
        bootstrappedTenantsRef.current.add(activeTenantId);
        return;
      }

      void bootstrapDefaults
        .mutateAsync(payload)
        .then(() => {
          bootstrappedTenantsRef.current.add(activeTenantId);
        })
        .catch(() => {
          bootstrappedTenantsRef.current.delete(activeTenantId);
        });
    },
    [activeTenantId, bootstrapDefaults, dataScope, filterDefaults, mode, parsedSearch, permissions],
  );

  const draftComplete =
    mode === "competitors"
      ? isCompetitorFiltersComplete(competitorFilters)
      : isMyBrandFiltersComplete(myBrandFilters);
  const competitorOptionsReady = mode !== "competitors" || competitorBrandsSuccess;

  const overviewDirty = useOverviewFiltersStore(isOverviewDirty);
  const applyOverviewFilters = useOverviewFiltersStore((s) => s.applyFilters);
  const drilldownDirty = useDrilldownFiltersStore((s) => isDrilldownDirty(s, mode));
  const hasDrilldownFilterLogicError = useDrilldownFiltersStore((s) =>
    hasCrossLevelOr(mode === "my-brand" ? s.myBrandAdvancedFilters : s.advancedFilters),
  );
  const combinedIsDirty =
    isDirty ||
    (isOverviewRoute && overviewDirty) ||
    (isDrilldownRoute && drilldownDirty);
  const canApplyCombined = canApplyExploreFilters({
    competitorOptionsReady,
    draftComplete,
    isOverviewRoute,
    overviewDirty,
    isDrilldownRoute,
    drilldownDirty,
    hasDrilldownFilterLogicError,
  });
  const applyDisabledReason = resolveExploreApplyDisableReason({
    canApply: canApplyCombined,
    competitorOptionsReady,
    competitorBrandsError,
    competitorBrandsLoading,
    hasDrilldownFilterLogicError: isDrilldownRoute && hasDrilldownFilterLogicError,
  });

  // Draft is source of truth while dirty — keep URL in sync (including effect-driven seeding).
  useEffect(() => {
    if (!isDirty) return;
    markStoreInitiatedNavigation();
    pushDraftToUrl({
      modeOverride: mode,
      competitorOverride: competitorFilters,
      myBrandOverride: myBrandFilters,
    });
  }, [isDirty, mode, competitorFilters, myBrandFilters, pushDraftToUrl, markStoreInitiatedNavigation]);

  useEffect(() => {
    if (!userTouchedRef.current && draftComplete && competitorOptionsReady && isDirty) {
      applyFilters();
      tryBootstrapDefaults(competitorFilters);
      markStoreInitiatedNavigation();
      pushAppliedToUrl({
        competitorOverride: competitorFilters,
        myBrandOverride: myBrandFilters,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftComplete, competitorOptionsReady, isDirty]);

  const [applyNonce, setApplyNonce] = useState(0);
  const [hasPendingPreset, setHasPendingPreset] = useState(false);

  const handleApply = () => {
    markStoreInitiatedNavigation();
    if (draftComplete && competitorOptionsReady) {
      applyFilters();
      tryBootstrapDefaults(competitorFilters);
      pushAppliedToUrl({
        competitorOverride: competitorFilters,
        myBrandOverride: myBrandFilters,
      });
    }
    if (competitorOptionsReady && isOverviewRoute) applyOverviewFilters();
    if (competitorOptionsReady && isDrilldownRoute && !hasDrilldownFilterLogicError) {
      applyDrilldownFilters();
      pushDrilldownAppliedToUrl();
    }
    setApplyNonce((n) => n + 1);
  };

  const handleDiscard = () => {
    discardFilters();
    if (isOverviewRoute) discardOverviewFilters();
    if (isDrilldownRoute) discardDrilldownFilters();
    markStoreInitiatedNavigation();
    pushAppliedToUrl();
    if (isDrilldownRoute) {
      pushDrilldownAppliedToUrl();
    }
  };

  const handleDateSelectionChange = useCallback(
    (selection: ExploreDateSelectionFields) => {
      // Same user-touched mark as the other header controls, so auto-apply does
      // not also commit unrelated dirty fields and Discard can still restore them.
      userTouchedRef.current = true;
      commitDateSelection(mode, selection);
      // Picker Apply is the date commit — queries key on applied dates, so the
      // grid must refetch here rather than waiting for ExploreApplyControls.
      markStoreInitiatedNavigation();
      const committed = useDashboardFiltersStore.getState();
      pushAppliedToUrl({
        competitorOverride: committed.competitorFilters,
        myBrandOverride: committed.myBrandFilters,
      });
      if (!isDrilldownRoute) return;
      // Write the window into the drilldown store in this same turn so the
      // query key and the request body agree — waiting for the lookback effect
      // would refetch once with the new header filters and the previous dates.
      const drilldown = useDrilldownFiltersStore.getState();
      if (mode === "my-brand") {
        initializeDrilldownDateRange(
          drilldown.appliedStartDate,
          drilldown.appliedEndDate,
          selection.startDate,
          selection.endDate,
        );
      } else {
        initializeDrilldownDateRange(
          selection.startDate,
          selection.endDate,
          drilldown.appliedMyBrandStartDate,
          drilldown.appliedMyBrandEndDate,
        );
      }
      pushDrilldownAppliedToUrl();
    },
    [
      mode,
      commitDateSelection,
      markStoreInitiatedNavigation,
      pushAppliedToUrl,
      isDrilldownRoute,
      initializeDrilldownDateRange,
      pushDrilldownAppliedToUrl,
    ],
  );

  const handleApplyPreset = useCallback(
    (preset: FilterPresetDto) => {
      markStoreInitiatedNavigation();
      if (preset.filters.mode === "my-brand") {
        pushAppliedToUrl({ myBrandOverride: withSnapshotDates(preset.filters.global) });
      } else {
        pushAppliedToUrl({ competitorOverride: withSnapshotDates(preset.filters.global) });
      }
    },
    [markStoreInitiatedNavigation, pushAppliedToUrl],
  );

  const handleApplyDrilldownPreset = useCallback(
    (preset: DrilldownFilterPresetDto) => {
      markStoreInitiatedNavigation();
      if (preset.filters.mode === "my-brand") {
        pushAppliedToUrl({ myBrandOverride: withSnapshotDates(preset.filters.global) });
      } else {
        pushAppliedToUrl({ competitorOverride: withSnapshotDates(preset.filters.global) });
      }
      pushDrilldownAppliedToUrl();
    },
    [markStoreInitiatedNavigation, pushAppliedToUrl, pushDrilldownAppliedToUrl],
  );

  const applyControls = (
    <ExploreApplyControls
      isDirty={combinedIsDirty}
      canApply={canApplyCombined}
      hasPendingPreset={hasPendingPreset}
      disableReason={applyDisabledReason}
      onApply={handleApply}
      onDiscard={handleDiscard}
    />
  );

  const pageFilters = isOverviewRoute
    ? (
        <OverviewFilterBar
          trailingActions={applyControls}
          onPresetApplied={handleApplyPreset}
          filtersHydrated={urlHydrationPhase !== "pending"}
          applySignal={applyNonce}
          onPendingPresetChange={setHasPendingPreset}
          canSavePreset={!combinedIsDirty}
        />
      )
    : isDrilldownRoute
      ? (
          <DrilldownFiltersToolbar
            trailingActions={applyControls}
            onPresetApplied={handleApplyDrilldownPreset}
            filtersHydrated={urlHydrationPhase !== "pending"}
            applySignal={applyNonce}
            onPendingPresetChange={setHasPendingPreset}
            canSavePreset={!combinedIsDirty}
          />
        )
      : undefined;

  const handleCompetitorFilterChange = <K extends keyof CompetitorFilters>(
    key: K,
    value: CompetitorFilters[K],
  ) => {
    userTouchedRef.current = true;
    setCompetitorFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "myIndustry") {
        industrySeededRef.current = false;
        next.myBrand = null;
        next.industryProducts = [];
      }
      if (key === "competitorsIndustry" || key === "platforms") {
        next.mainCompetitor = null;
        next.benchmark = [];
      }
      return next;
    });
  };

  const handleMyBrandFilterChange = <K extends keyof MyBrandFilters>(
    key: K,
    value: MyBrandFilters[K],
  ) => {
    userTouchedRef.current = true;
    setMyBrandFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "platforms" || key === "accounts") {
        const pruned = pruneMyBrandSelections(
          filterTuples,
          {
            platforms: next.platforms,
            accounts: next.accounts,
            products: next.products,
          },
          key,
        );
        return { ...next, ...pruned };
      }
      return next;
    });
  };

  return (
    <GlobalFilterHeader
      mode={mode}
      onModeChange={handleModeChange}
      showModeToggle={modeAccess.showToggle}
      isDirty={combinedIsDirty}
      canApply={canApplyCombined}
      applyDisabledReason={applyDisabledReason}
      onApply={handleApply}
      onDiscard={handleDiscard}
      onDateSelectionChange={handleDateSelectionChange}
      competitorFilters={competitorFilters}
      onCompetitorFilterChange={handleCompetitorFilterChange}
      myBrandFilters={myBrandFilters}
      onMyBrandFilterChange={handleMyBrandFilterChange}
      industries={industries}
      industriesLoading={industriesLoading}
      competitorDefaultsLoading={competitorDefaultsLoading}
      myIndustryBrands={myIndustryBrands}
      myIndustryBrandsLoading={myIndustryBrandsLoading}
      competitorBrands={competitorBrands}
      competitorBrandsLoading={competitorBrandsLoading}
      competitorBrandsError={competitorBrandsError}
      products={myBrandProductOptions}
      productsLoading={filterOptionsLoading}
      productsToCompare={industryProducts}
      productsToCompareLoading={productsToCompareLoading}
      accounts={myBrandAccounts}
      recentAccounts={recentAccounts}
      onAccountRecorded={onAccountRecorded}
      recentProducts={recentProducts}
      onProductRecorded={onProductRecorded}
      adPlatforms={myBrandPlatforms}
      adPlatformsLoading={filterOptionsLoading}
      competitorPlatforms={competitorPlatforms}
      competitorPlatformsLoading={competitorPlatformsLoading}
      competitorPlatformsError={competitorPlatformsError}
      competitorPlatformsDisabled={!competitorFilters.competitorsIndustry}
      northStar={northStar}
      onNorthStarChange={setNorthStar}
      pageFilters={pageFilters}
    />
  );
}
